import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { CheckCircle2, Clock, FileText, Eye, Loader2, AlertTriangle, XCircle, ShieldCheck, ArrowRight, Lock } from 'lucide-react';
import { openSecureDoc } from '../components/kyc/DocumentCard';
import { kycMeta, normalizeKyc, formatDate, DOC_STATUS_META } from '../lib/kyc';

const Stat = ({ label, children, testId }) => (
  <div className="p-4 rounded-sm border border-border/50 bg-card/50" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <div className="font-medium text-sm">{children}</div>
  </div>
);

export default function KYCStatusPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/merchants/me/kyc/status');
        const st = normalizeKyc(r.data.status);
        if (st === 'NOT_STARTED' || st === 'DRAFT') { navigate('/kyc', { replace: true }); return; }
        setState(r.data);
      } catch { toast.error('Unable to load KYC status'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex items-center justify-center min-h-[50vh] text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>;
  if (!state) return null;

  const status = normalizeKyc(state.status);
  const meta = kycMeta(status);
  const inReview = status === 'SUBMITTED' || status === 'UNDER_REVIEW';
  const Icon = { APPROVED: CheckCircle2, REJECTED: XCircle, REUPLOAD_REQUIRED: AlertTriangle }[status] || Clock;
  const tone = { APPROVED: 'text-green-500 bg-green-500/10', REJECTED: 'text-red-500 bg-red-500/10', REUPLOAD_REQUIRED: 'text-orange-500 bg-orange-500/10' }[status] || 'text-amber-500 bg-amber-500/10';
  const headline = { APPROVED: 'KYC Approved', REJECTED: 'KYC Rejected', REUPLOAD_REQUIRED: 'Action Required' }[status] || 'KYC Submitted';
  const message = {
    APPROVED: 'Your merchant KYC has been approved. Payout access is now available subject to your account limits and applicable controls.',
    REJECTED: 'Your KYC application was not approved. Please review the reason below and resubmit.',
    REUPLOAD_REQUIRED: 'Our verification team has requested updated documents. Please upload the requested documents to continue.',
  }[status] || 'Your KYC documents have already been submitted successfully. Our team is reviewing your documents. You do not need to upload them again unless our team requests a re-upload.';

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-stagger" data-testid="kyc-status-page">
      <div>
        <h1 className="text-2xl font-heading font-bold">KYC Verification</h1>
        <p className="text-muted-foreground text-sm mt-1">Merchant ID <span className="font-mono">{state.mid}</span>{state.applicationId && <> · Application ID <span className="font-mono" data-testid="kyc-application-id">{state.applicationId}</span></>}</p>
      </div>

      <Card className="card-grid" data-testid="kyc-status-card">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${tone}`}><Icon className="h-7 w-7" /></div>
            <div className="flex-1">
              <h2 className="text-xl font-heading font-semibold" data-testid="kyc-status-headline">{headline}</h2>
              <p className="text-sm text-muted-foreground mt-1">{message}</p>
              {state.remarks && (
                <div className="mt-3 p-3 rounded-sm border border-border/60 bg-muted/40 text-sm" data-testid="kyc-remarks-text">
                  <span className="text-muted-foreground">Reason: </span>{state.remarks}
                </div>
              )}
            </div>
            <span className={`inline-flex items-center gap-2 text-xs px-2.5 py-1 rounded-sm border shrink-0 ${meta.badge}`} data-testid="kyc-status-badge">
              <span className={`w-2 h-2 rounded-full ${meta.dot} ${inReview ? 'animate-pulse' : ''}`} /> {meta.label}
            </span>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-6">
            <Stat label="Status" testId="kyc-stat-status"><span className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${meta.dot}`} />{meta.label}</span></Stat>
            <Stat label="Submitted" testId="kyc-stat-submitted">{formatDate(state.submittedAt)}</Stat>
            <Stat label="Documents" testId="kyc-stat-documents">{state.documents.length} submitted</Stat>
            <Stat label="Verification" testId="kyc-stat-verification">{status === 'APPROVED' ? `Approved ${formatDate(state.reviewedAt)}` : status === 'REJECTED' ? `Rejected ${formatDate(state.reviewedAt)}` : status === 'REUPLOAD_REQUIRED' ? 'Awaiting your update' : 'Pending admin review'}</Stat>
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            {status === 'APPROVED' && <Button onClick={() => navigate('/dashboard/payouts/create')} className="rounded-sm glow-primary" data-testid="kyc-create-payout-btn">Create Payout <ArrowRight className="h-4 w-4 ml-2" /></Button>}
            {status === 'REJECTED' && <Button onClick={() => navigate('/kyc')} className="rounded-sm" data-testid="kyc-resubmit-btn">Update & Resubmit <ArrowRight className="h-4 w-4 ml-2" /></Button>}
            {status === 'REUPLOAD_REQUIRED' && <Button onClick={() => navigate('/kyc')} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="kyc-reupload-btn">Upload Updated Documents <ArrowRight className="h-4 w-4 ml-2" /></Button>}
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="rounded-sm" data-testid="kyc-go-dashboard-btn">Go to Dashboard</Button>
          </div>
          {!state.payoutEnabled && (
            <p className="flex items-center gap-2 text-xs text-muted-foreground mt-4" data-testid="kyc-payout-locked-note"><Lock className="h-3.5 w-3.5" /> Payouts are locked until KYC verification is completed{status === 'APPROVED' ? ' and payout service is activated by our team' : ''}.</p>
          )}
        </CardContent>
      </Card>

      <Card className="card-grid" data-testid="kyc-documents-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading flex items-center gap-2"><FileText className="h-5 w-5" /> Submitted Documents</CardTitle>
          <CardDescription>{inReview ? 'Your documents have already been submitted. Please wait for our verification team to complete the review.' : 'Documents attached to this KYC application.'}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {state.documents.length === 0 && <p className="text-sm text-muted-foreground">No documents on file.</p>}
          {state.documents.map(d => {
            const dm = DOC_STATUS_META[d.status] || DOC_STATUS_META.uploaded;
            const flagged = ['rejected', 'reupload_required'].includes(d.status);
            return (
              <div key={d.document_id} className={`flex items-center justify-between gap-3 p-3 rounded-sm border ${flagged ? 'border-orange-500/40 bg-orange-500/5' : 'border-border/50'}`} data-testid={`status-doc-${d.doc_type}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {flagged ? <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" /> : <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />}
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{d.doc_label}</p>
                    <p className="text-xs text-muted-foreground truncate">{d.original_filename}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className={`text-xs ${dm.cls}`} data-testid={`status-doc-status-${d.doc_type}`}>{inReview && d.status === 'uploaded' ? 'Submitted' : dm.label}</span>
                  <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openSecureDoc(api, d.view_url)} data-testid={`status-doc-view-${d.doc_type}`}><Eye className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {status === 'APPROVED' && (
        <div className="flex items-center gap-2 text-sm text-green-600" data-testid="kyc-approved-footer"><ShieldCheck className="h-4 w-4" /> Verified merchant · {state.businessName}</div>
      )}
    </div>
  );
}
